import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { sessionEnded } from 'features/room/actions';
import { signalingUserJoined } from 'features/users/actions';
import { selectAvailableUserIdsWithoutLocal } from 'features/users/usersSlice';
import { UserId } from 'features/users/types';
import { RootState } from 'store/store';

export interface LocalMuteState {
  mutedUserIds: Record<UserId, true>;
  isolatedUserId: UserId | null;
}

export const initialState: LocalMuteState = {
  mutedUserIds: {},
  isolatedUserId: null,
};

const localMuteSlice = createSlice({
  name: 'localMute',
  initialState,
  reducers: {
    userMutedLocally(state, action: PayloadAction<UserId>) {
      state.mutedUserIds[action.payload] = true;
    },
    userUnmutedLocally(state, action: PayloadAction<UserId>) {
      if (!state.mutedUserIds[action.payload]) {
        return;
      }

      delete state.mutedUserIds[action.payload];
      // unmuting anyone drops you back to manual muting, so newcomers join unmuted
      state.isolatedUserId = null;
    },
    audioIsolated(
      state,
      action: PayloadAction<{ focusUserId: UserId; mutedUserIds: Record<UserId, true> }>
    ) {
      state.isolatedUserId = action.payload.focusUserId;
      state.mutedUserIds = action.payload.mutedUserIds;
    },
    hearEveryoneRequested() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingUserJoined, (state, action) => {
        // the isolated user rejoins on every room move; muting them would silence the whole room
        if (state.isolatedUserId && state.isolatedUserId !== action.payload.id) {
          state.mutedUserIds[action.payload.id] = true;
        }
      })
      .addCase(sessionEnded, () => initialState);
  },
});

export const { userMutedLocally, userUnmutedLocally, audioIsolated, hearEveryoneRequested } =
  localMuteSlice.actions;

export const selectLocallyMutedUserIds = (state: RootState) => state.localMute.mutedUserIds;
export const selectIsUserLocallyMuted = (state: RootState, userId: UserId) =>
  !!state.localMute.mutedUserIds[userId];
export const selectIsolatedUserId = (state: RootState) => state.localMute.isolatedUserId;
// mutes outlive room moves, so only the ones you can currently hear should drive the UI
export const selectIsAnyLocalMuteActive = createSelector(
  [selectLocallyMutedUserIds, selectAvailableUserIdsWithoutLocal],
  (mutedUserIds, userIds) => userIds.some((id) => mutedUserIds[id])
);

export default localMuteSlice.reducer;
